import React from 'react';
import { Pressable } from 'react-native';

import { Box, Text, useTheme } from 'app/shopify/theme';

export type RadioButtonSize = 'small' | 'large';
export type RadioButtonState = 'enabled' | 'hover' | 'pressed' | 'focus' | 'disabled';

export interface RadioButtonProps {
  label?: string;
  description?: string;
  showDescription?: boolean;
  selected?: boolean;
  size?: RadioButtonSize;
  state?: RadioButtonState;
  onPress?: () => void;
}

interface PressableInteractionState {
  pressed: boolean;
  hovered?: boolean;
}

export function RadioButton({
  label = 'Radio label',
  description = 'Description',
  showDescription = false,
  selected = false,
  size = 'small',
  state = 'enabled',
  onPress,
}: RadioButtonProps) {
  const theme = useTheme();
  const isLarge = size === 'large';
  const isDisabled = state === 'disabled';
  const isInteractive = state === 'enabled';

  const controlSize = 24;
  const labelWidth = isLarge ? 95 : 83;
  const labelVariant = isLarge ? 'bodyLarge' : 'bodyBase';
  const borderWidth = 1;

  function getControlBackgroundColor() {
    if (isDisabled) return theme.colors['surface.disabled'];
    return theme.colors['surface.default'];
  }

  function getBorderColor(hovered: boolean, pressed: boolean) {
    if (isDisabled) return theme.colors['border.disabled'];
    if (state === 'hover' || state === 'pressed' || hovered || pressed) return theme.colors['border.brand'];
    if (selected) return theme.colors['border.selected'];
    return theme.colors['border.primary'];
  }

  function getDotColor() {
    if (isDisabled) return theme.colors['border.disabled'];
    return theme.colors['border.selected'];
  }

  function getLabelColor() {
    if (isDisabled) return theme.colors['text.disabled'];
    return theme.colors['text.primary'];
  }

  function getDescriptionColor() {
    if (isDisabled) return theme.colors['text.disabled'];
    return theme.colors['text.secondary'];
  }

  function getOverlayColor(showHover: boolean, showPressed: boolean) {
    if (showPressed) return theme.colors['state-layer.press-brand'];
    if (showHover) return theme.colors['state-layer.hover-brand'];
    return undefined;
  }

  function renderInner(interaction?: PressableInteractionState) {
    const hovered = interaction?.hovered ?? false;
    const pressed = interaction?.pressed ?? false;

    const showHover = !isDisabled && (state === 'hover' || (isInteractive && hovered && !pressed));
    const showPressed = !isDisabled && (state === 'pressed' || (isInteractive && pressed));
    const overlayColor = getOverlayColor(showHover, showPressed);

    return (
      <Box position="relative" flexDirection="row" gap="spacing.general.x3" alignItems="flex-start">
        {overlayColor ? (
          <Box
            position="absolute"
            top={-8}
            bottom={-8}
            left={-16}
            right={-16}
            style={{ backgroundColor: overlayColor }}
          />
        ) : null}

        <Box
          position="relative"
          width={controlSize}
          height={controlSize}
          borderRadius="radius.full"
          alignItems="center"
          justifyContent="center"
          borderWidth={borderWidth}
          style={{
            backgroundColor: getControlBackgroundColor(),
            borderColor: getBorderColor(showHover, showPressed),
          }}
        >
          {selected ? (
            <Box width={12} height={12} borderRadius="radius.full" style={{ backgroundColor: getDotColor() }} />
          ) : null}

          {state === 'focus' && !isDisabled ? (
            <Box
              position="absolute"
              top={-3}
              left={-3}
              right={-3}
              bottom={-3}
              borderRadius="radius.full"
              borderWidth={1}
              borderColor="border.brand-alt"
            />
          ) : null}
        </Box>

        <Box minHeight={24} justifyContent="center" gap="spacing.general.x1" width={labelWidth}>
          <Text
            variant={labelVariant}
            style={{
              color: getLabelColor(),
              fontFamily: isLarge ? theme.fonts.theFuture.medium : theme.textVariants[labelVariant].fontFamily,
            }}
          >
            {label}
          </Text>
          {showDescription ? (
            <Text variant="bodySmall" style={{ color: getDescriptionColor() }}>
              {description}
            </Text>
          ) : null}
        </Box>
      </Box>
    );
  }

  if (isInteractive) {
    return (
      <Pressable onPress={onPress}>
        {(pressableState) =>
          renderInner({
            pressed: pressableState.pressed,
            hovered: 'hovered' in pressableState ? (pressableState as PressableInteractionState).hovered : false,
          })
        }
      </Pressable>
    );
  }

  return <Box>{renderInner()}</Box>;
}
